//@ts-nocheck
import { routingMixin } from "@jack-henry/web-component-router";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/cards/jha-card/jha-card";
import {
  fixedEncodeURIComponent,
  getRequest,
  putRequest,
} from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { GlobalVariables, UserTypes } from "../../utils/constants";
import {
  decryptValue,
  showToastMessage,
} from "../../utils/app-utils";
import {
  dateTimeFieldLabel,
  formatDateTimeForDisplay,
} from "../../utils/datetime-display";
import { AppRouters } from "../../utils/app-router";
import { isSessionBusinessUnitAllowed } from "../../utils/business-unit-session";
import {
  InstitutionType,
  InstitutionListResponse,
  normalizeInstitution,
} from "../../types/BusinessUnits/InstitutionType";
import Cookies from "js-cookie";
import "../../Components/Loader";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import { StatusConstants } from "../../utils/apiStatusConstants";
import { requestInstitutionsListRefresh } from "../../utils/institution-list-refresh";
import {
  INSTITUTION_CONCURRENCY_USER_MESSAGE,
  isInstitutionConcurrencyError,
} from "../../utils/institution-concurrency";
import { FeatureFlags } from "../../utils/launchdarkly-config";
import { launchDarkly } from "../../utils/launchdarkly-service";
import { isInitialInstitutionFeatureEnabled } from "../../utils/initial-institution-feature-gate";

@customElement("institution-detail-view")
export class InstitutionDetailView extends routingMixin(LitElement) {
  static get is() {
    return "institution-detail-view";
  }

  @property({ reflect: true, attribute: "business-unit-id" })
  businessUnitId = "";

  @property({ reflect: true, attribute: "institution-uuid" })
  institutionUuid = "";

  @property({ type: Boolean }) apiLoading = false;
  @property({ type: String })
  loggedUserType = decryptValue(Cookies.get("userType") as string);
  @property({ type: Boolean }) isReadOnlyUser = /readonly/i.test(
    decryptValue(Cookies.get("userRole") || "")
  );
  @property({ type: Boolean }) isEnterpriseAdmin = /enterpriseadmin/i.test(
    decryptValue(Cookies.get("userType") || "")
  );

  @property({ type: Object }) institution: InstitutionType | null = null;
  @property({ type: String }) businessUnitName = "";
  @property({ type: String }) returnFilterEnvironment = "";
  @property({ type: String }) returnFilterDefault = "";

  private institutionLoadKey = "";
  @state() private setDefaultInProgress = false;
  @state() private setDefaultDialogActive = false;
  private readonly allRecords = 100000;

  private static readonly INSTITUTION_ALREADY_DEFAULT_MESSAGE =
    "This institution is already the default.";

  private static readonly SET_DEFAULT_GENERIC_MESSAGE =
    "Setting this institution as default will replace the current default institution for this business unit. Do you want to continue?";

  static readonly styles = css`
    #institutionDetail {
      padding: 24px;
      height: -webkit-fill-available;
      display: flex;
      flex-direction: column;
    }
    #breadcrumb {
      font-size: 14px;
      color: var(--jha-color-secondary-text);
      margin-bottom: 8px;
    }
    #breadcrumb a {
      color: var(--jha-color-primary);
      text-decoration: none;
      cursor: pointer;
    }
    #breadcrumb a:hover {
      text-decoration: underline;
    }
    jha-card {
      padding: 24px;
      width: -webkit-fill-available;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .field-grid {
      display: grid;
      grid-template-columns: 200px 1fr;
      gap: 12px 24px;
      margin-bottom: 24px;
    }
    .field-label {
      font-weight: 600;
      color: var(--jha-color-secondary-text);
      font-size: 14px;
    }
    .field-value {
      font-size: 14px;
    }
    .default-badge {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      background-color: var(--jha-color-success-light, #d4edda);
      color: var(--jha-color-success, #155724);
    }
    h2 {
      margin-bottom: 18px;
      font-size: 24px;
      font-weight: 600;
    }
    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }
    #btnCon {
      margin-top: auto;
      display: flex;
      flex-direction: row;
      gap: 15px;
    }
  `;

  get canManageInstitutions(): boolean {
    if (this.isReadOnlyUser) {
      return false;
    }
    if (this.isEnterpriseAdmin) {
      return true;
    }
    return this.loggedUserType?.toLowerCase() === UserTypes.businessadmin;
  }

  get showInstitutionManagementActions(): boolean {
    return this.canManageInstitutions && isInitialInstitutionFeatureEnabled();
  }

  private handleInitialInstitutionFlagChange = (): void => {
    this.requestUpdate();
  };

  private refreshFlagValues = (): void => {
    this.requestUpdate();
  };

  private formatDateTime(val: string | undefined): string {
    if (!val) return "";
    return formatDateTimeForDisplay(val);
  }

  onBackToInstitutions() {
    let url = `/business-units/${fixedEncodeURIComponent(
      this.businessUnitId
    )}/institutions`;
    const params: string[] = [];
    if (this.returnFilterEnvironment) params.push(`environment=${encodeURIComponent(this.returnFilterEnvironment)}`);
    if (this.returnFilterDefault) params.push(`defaultStatus=${encodeURIComponent(this.returnFilterDefault)}`);
    if (params.length) url += `?${params.join("&")}`;
    AppRouters.go(url);
  }

  onBackToBusinessUnits() {
    AppRouters.go("/business-units");
  }

  private buildSetDefaultConfirmMessage(
    currentDefault: InstitutionType | undefined,
    targetUuid: string
  ): string {
    if (
      currentDefault?.institutionUuid &&
      currentDefault.institutionUuid !== targetUuid &&
      currentDefault.isDefault
    ) {
      return `Setting this institution as default will replace the current default institution (RT ID: ${currentDefault.institutionRtId}, Environment: ${currentDefault.institutionEnvironment}). Do you want to continue?`;
    }
    return InstitutionDetailView.SET_DEFAULT_GENERIC_MESSAGE;
  }

  private async findCurrentDefaultInstitution(
    targetUuid: string
  ): Promise<InstitutionType | undefined> {
    try {
      const listUrl =
        Apis.INSTITUTION_ALL +
        `?businessUnitId=${fixedEncodeURIComponent(this.businessUnitId)}` +
        `&count=${this.allRecords}`;
      const response = await getRequest<InstitutionListResponse>(listUrl);
      if (!response || response instanceof Response) {
        return undefined;
      }
      if (response.messageStatus !== null) {
        return undefined;
      }
      const institutions = (response.institutions || []).map(
        (row: Record<string, unknown>) => normalizeInstitution(row)
      );
      return institutions.find(
        (i) => i.isDefault && i.institutionUuid !== targetUuid
      );
    } catch {
      return undefined;
    }
  }

  async onSetDefaultInstitution() {
    if (
      !this.institutionUuid ||
      !this.businessUnitId ||
      this.apiLoading ||
      this.setDefaultInProgress ||
      this.setDefaultDialogActive
    ) {
      return;
    }
    if (!this.canManageInstitutions) {
      showToastMessage(
        "You do not have permission to update institutions.",
        "danger"
      );
      return;
    }
    if (!isInitialInstitutionFeatureEnabled()) {
      showToastMessage(
        "Institution management is not available.",
        "danger"
      );
      return;
    }
    if (this.institution?.isDefault) {
      return;
    }

    this.setDefaultDialogActive = true;
    try {
      const currentDefault = await this.findCurrentDefaultInstitution(
        this.institutionUuid
      );
      const confirmMessage = this.buildSetDefaultConfirmMessage(
        currentDefault,
        this.institutionUuid
      );

      const result = await openJhftDialog({
        variant: "info",
        title: "Set as default institution",
        message: confirmMessage,
        cancelLabel: "Cancel",
        confirmLabel: "Set as default",
      });
      if (result !== "confirm") {
        return;
      }
    } finally {
      this.setDefaultDialogActive = false;
    }

    this.setDefaultInProgress = true;
    try {
      const getUrl =
        Apis.INSTITUTION_GET +
        `/${fixedEncodeURIComponent(this.institutionUuid)}` +
        `?businessUnitId=${fixedEncodeURIComponent(this.businessUnitId)}`;
      const institutionData = await getRequest(getUrl);

      if (!institutionData) {
        return;
      }

      if (institutionData instanceof Response) {
        if (institutionData.status === StatusConstants.NOT_FOUND_USER_CODE) {
          showToastMessage("Institution not found.", "danger");
        } else if (
          institutionData.status === StatusConstants.UNAUTHORIZED_USER_CODE
        ) {
          showToastMessage(
            "You do not have permission to update this institution.",
            "danger"
          );
        } else {
          showToastMessage("Something went wrong, please try again.", "danger");
        }
        return;
      }

      if (institutionData.messageStatus != null) {
        const errMsg = getErrorMessage(institutionData.details);
        showToastMessage(
          errMsg.trim() || "Something went wrong, please try again.",
          "danger"
        );
        return;
      }

      const inst = normalizeInstitution(institutionData);
      if (
        !inst.institutionUuid ||
        !inst.businessUnitId ||
        inst.businessUnitId !== this.businessUnitId
      ) {
        showToastMessage("Institution not found.", "danger");
        return;
      }

      if (inst.isDefault) {
        showToastMessage(
          InstitutionDetailView.INSTITUTION_ALREADY_DEFAULT_MESSAGE,
          "success"
        );
        if (this.returnFilterDefault === "false") {
          this.returnFilterDefault = "";
        }
        requestInstitutionsListRefresh(this.businessUnitId);
        await this.loadInstitutionDetail();
        return;
      }

      const eTag = inst.eTag ?? "";
      if (!eTag) {
        showToastMessage(
          "Institution version is missing. Reload and try again.",
          "danger"
        );
        return;
      }

      const updateUrl =
        Apis.INSTITUTION_UPDATE +
        `/${fixedEncodeURIComponent(this.institutionUuid)}` +
        `?businessUnitId=${fixedEncodeURIComponent(this.businessUnitId)}`;
      const putBody = {
        institutionRtId: inst.institutionRtId,
        institutionEnvironment: inst.institutionEnvironment,
        consumerNameProduct: inst.consumerNameProduct,
        isDefault: true,
        updatedBy: decryptValue(Cookies.get("userId") || ""),
      };
      const response = await putRequest(updateUrl, eTag, putBody);

      if (!response) {
        return;
      }

      const httpStatus =
        response instanceof Response ? response.status : response?.status;
      if (httpStatus === StatusConstants.UNAUTHORIZED_USER_CODE) {
        showToastMessage(
          "You do not have permission to update this institution.",
          "danger"
        );
        return;
      }
      if (httpStatus === StatusConstants.NOT_FOUND_USER_CODE) {
        showToastMessage("Institution not found.", "danger");
        return;
      }
      if (httpStatus === 412) {
        showToastMessage(INSTITUTION_CONCURRENCY_USER_MESSAGE, "danger");
        return;
      }
      if (response instanceof Response) {
        showToastMessage("Something went wrong, please try again.", "danger");
        return;
      }

      if (response?.messageStatus === null) {
        showToastMessage("Default institution updated successfully.", "success");
        if (this.returnFilterDefault === "false") {
          this.returnFilterDefault = "";
        }
        requestInstitutionsListRefresh(this.businessUnitId);
        await this.loadInstitutionDetail();
        return;
      }

      if (response?.details?.length > 0) {
        if (isInstitutionConcurrencyError(response.details)) {
          showToastMessage(INSTITUTION_CONCURRENCY_USER_MESSAGE, "danger");
        } else {
          showToastMessage(getErrorMessage(response.details), "danger");
        }
        return;
      }

      const errMsg = getErrorMessage(response?.details);
      showToastMessage(
        errMsg.trim() || "Something went wrong, please try again.",
        "danger"
      );
    } catch {
      showToastMessage("Something went wrong, please try again.", "danger");
    } finally {
      this.setDefaultInProgress = false;
    }
  }

  onEditInstitution() {
    if (!this.institutionUuid || !this.businessUnitId) return;
    if (!isInitialInstitutionFeatureEnabled()) {
      showToastMessage(
        "Institution management is not available.",
        "danger"
      );
      return;
    }
    if (!this.canManageInstitutions) {
      showToastMessage(
        "You do not have permission to edit institutions.",
        "danger"
      );
      return;
    }
    let url = `/business-units/${fixedEncodeURIComponent(
      this.businessUnitId
    )}/institutions/edit-institution/${fixedEncodeURIComponent(
      this.institutionUuid
    )}`;
    const params: string[] = [];
    if (this.returnFilterEnvironment) {
      params.push(
        `environment=${encodeURIComponent(this.returnFilterEnvironment)}`
      );
    }
    if (this.returnFilterDefault) {
      params.push(
        `defaultStatus=${encodeURIComponent(this.returnFilterDefault)}`
      );
    }
    if (params.length) url += `?${params.join("&")}`;
    AppRouters.go(url);
  }

  private async verifyBusinessUnitAccess(): Promise<boolean> {
    try {
      const buData = await getRequest(
        Apis.GET_BUSINESSUNIT +
        `/${fixedEncodeURIComponent(this.businessUnitId)}`
      );

      if (!buData) {
        showToastMessage("Something went wrong, please try again.", "danger");
        AppRouters.go("/business-units");
        return false;
      }

      if (buData instanceof Response) {
        if (buData.status === 401) {
          showToastMessage(
            "You do not have permission to view this Business Unit.",
            "danger"
          );
        } else if (buData.status === 404) {
          showToastMessage(
            "Business Unit not found.",
            "danger"
          );
        } else {
          showToastMessage(
            "Something went wrong, please try again.",
            "danger"
          );
        }
        AppRouters.go("/business-units");
        return false;
      }

      if (
        this.loggedUserType?.toLowerCase() === UserTypes.businessadmin &&
        !isSessionBusinessUnitAllowed(buData)
      ) {
        showToastMessage(
          "You do not have permission to view this Business Unit.",
          "danger"
        );
        AppRouters.go("/business-units");
        return false;
      }

      this.businessUnitName =
        buData.businessUnitName ?? buData.BusinessUnitName ?? "";
      return true;
    } catch {
      showToastMessage(
        "Something went wrong, please try again.",
        "danger"
      );
      AppRouters.go("/business-units");
      return false;
    }
  }

  private async loadInstitutionDetail() {
    if (!this.institutionUuid || !this.businessUnitId) {
      showToastMessage("Missing institution or business unit identifier.", "danger");
      this.onBackToInstitutions();
      return;
    }
    this.institution = null;
    this.apiLoading = true;
    const loadKey = `${this.businessUnitId}:${this.institutionUuid}`;
    this.institutionLoadKey = loadKey;
    try {
      const url =
        Apis.INSTITUTION_GET +
        `/${fixedEncodeURIComponent(this.institutionUuid)}` +
        `?businessUnitId=${fixedEncodeURIComponent(this.businessUnitId)}`;

      const response = await getRequest(url);

      if (loadKey !== this.institutionLoadKey) {
        return;
      }

      if (!response) {
        this.institution = null;
        this.onBackToInstitutions();
        return;
      }

      if (response instanceof Response && response.status === 404) {
        showToastMessage(
          "Institution not found.",
          "danger"
        );
        this.onBackToInstitutions();
        return;
      }

      if (response instanceof Response && response.status === 401) {
        showToastMessage(
          "You do not have permission to view this institution.",
          "danger"
        );
        this.onBackToInstitutions();
        return;
      }

      if (response.messageStatus === null) {
        const inst = normalizeInstitution(response);
        if (
          inst.institutionUuid &&
          inst.businessUnitId &&
          inst.businessUnitId === this.businessUnitId
        ) {
          this.institution = inst;
        } else {
          this.institution = null;
          showToastMessage("Institution not found.", "danger");
          this.onBackToInstitutions();
        }
      } else if (response.details?.length > 0) {
        this.institution = null;
        showToastMessage(getErrorMessage(response.details), "danger");
        this.onBackToInstitutions();
      } else {
        this.institution = null;
        showToastMessage(
          "Something went wrong loading institution details, please try again.",
          "danger"
        );
        this.onBackToInstitutions();
      }
    } catch {
      this.institution = null;
      showToastMessage(
        "Something went wrong loading institution details, please try again.",
        "danger"
      );
      this.onBackToInstitutions();
    } finally {
      if (loadKey === this.institutionLoadKey) {
        this.apiLoading = false;
      }
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    this.refreshFlagValues();
    launchDarkly.whenReady().then(() => this.refreshFlagValues());
    launchDarkly.on(FeatureFlags.INITIAL_INSTITUTION, this.handleInitialInstitutionFlagChange);
    document.title = GlobalVariables.MAIN_TITLE?.concat(
      " > Institution Detail"
    );
  }

  disconnectedCallback() {
    launchDarkly.off(FeatureFlags.INITIAL_INSTITUTION, this.handleInitialInstitutionFlagChange);
    super.disconnectedCallback();
  }

  async routeEnter(
    currentNode: unknown,
    nextNodeIfExists: unknown,
    routeId: string,
    context: unknown
  ) {
    await super.routeEnter(currentNode, nextNodeIfExists, routeId, context);
    if (!nextNodeIfExists) {
      this.institution = null;
      const params = new URLSearchParams(window.location.search);
      this.returnFilterEnvironment = params.get("environment") ?? "";
      const defaultParam = params.get("defaultStatus") ?? "";
      this.returnFilterDefault =
        defaultParam === "true" || defaultParam === "false"
          ? defaultParam
          : "";

      const allowed = await this.verifyBusinessUnitAccess();
      if (allowed) {
        await this.loadInstitutionDetail();
      }
    }
  }

  render() {
    const inst = this.institution;
    return html`
      <div id="institutionDetail">
        ${this.apiLoading || this.setDefaultInProgress
        ? html`<circle-loader></circle-loader>`
        : undefined}
        <div id="breadcrumb">
          <a @click=${this.onBackToBusinessUnits}>Business Units</a>
          &rsaquo;
          <a @click=${this.onBackToInstitutions}
            >${this.businessUnitName || this.businessUnitId} &rsaquo;
            Institutions</a
          >
          &rsaquo; Detail
        </div>
        <h1>Institution Detail</h1>
        <jha-card>
          ${inst
        ? html`
                <h2>Institution Information</h2>
                <div class="field-grid">
                  <span class="field-label">RT ID</span>
                  <span
                    class="field-value"
                    data-auto-id="institutionDetailRtId"
                    >${inst.institutionRtId}</span
                  >

                  <span class="field-label">UUID</span>
                  <span
                    class="field-value"
                    data-auto-id="institutionDetailUuid"
                    >${inst.institutionUuid}</span
                  >

                  <span class="field-label">Environment</span>
                  <span
                    class="field-value"
                    data-auto-id="institutionDetailEnvironment"
                    >${inst.institutionEnvironment}</span
                  >

                  <span class="field-label">Default</span>
                  <span
                    class="field-value"
                    data-auto-id="institutionDetailDefault"
                  >
                    ${inst.isDefault
            ? html`<span class="default-badge">Default</span>`
            : "No"}
                  </span>

                  <span class="field-label">Consumer Name / Product</span>
                  <span
                    class="field-value"
                    data-auto-id="institutionDetailConsumerName"
                    >${inst.consumerNameProduct}</span
                  >

                  <span class="field-label">Business Unit ID</span>
                  <span class="field-value">${inst.businessUnitId}</span>

                  <span class="field-label">Created By</span>
                  <span class="field-value">${inst.createdBy}</span>

                  <span class="field-label">${dateTimeFieldLabel("Created On")}</span>
                  <span class="field-value"
                    >${this.formatDateTime(inst.createDateTime)}</span
                  >

                  <span class="field-label">Updated By</span>
                  <span class="field-value">${inst.updatedBy}</span>

                  <span class="field-label">${dateTimeFieldLabel("Updated On")}</span>
                  <span class="field-value"
                    >${this.formatDateTime(inst.updateDateTime)}</span
                  >
                </div>
              `
        : html`${!this.apiLoading
          ? html`<p>No institution data available.</p>`
          : html``}`}
          <div id="btnCon">
            ${this.showInstitutionManagementActions && this.institution
        ? html`
                  <jha-button
                    data-auto-id="institutionDetailEditButton"
                    type="button"
                    primary
                    large
                    @click=${this.onEditInstitution}
                  >
                    Edit Institution
                  </jha-button>
                  ${!this.institution.isDefault
            ? html`
                        <jha-button
                          data-auto-id="institutionDetailSetDefaultButton"
                          type="button"
                          primary
                          large
                          ?disabled=${this.apiLoading ||
              this.setDefaultInProgress ||
              this.setDefaultDialogActive}
                          @click=${this.onSetDefaultInstitution}
                        >
                          Set as default
                        </jha-button>
                      `
            : html``}
                `
        : html``}
            <jha-button
              data-auto-id="institutionDetailBack"
              type="button"
              tertiary
              large
              @click=${this.onBackToInstitutions}
            >
              Back to Institutions
            </jha-button>
          </div>
        </jha-card>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "institution-detail-view": InstitutionDetailView;
  }
}
